import client from '@/services/graphql/client';
import { assert, CustomError } from '@common/errors';
import { PresignedPostForUserUploadDocument } from '@common/graphql/generated';
import { Attachment, PresignedPost } from '@common/types';

/**
 * Uploads a file to S3 using a presigned POST request
 *
 * Ref: https://docs.aws.amazon.com/AmazonS3/latest/API/sigv4-post-example.html
 */
export const uploadFile = async (file: File): Promise<Attachment> => {
    const graphqlResponse = await client.query({
        query: PresignedPostForUserUploadDocument,
        fetchPolicy: 'no-cache',
        context: { queryDeduplication: false },
    });
    const presignedPost = graphqlResponse.data
        .presignedPostForUserUpload as PresignedPost;

    const key = presignedPost.fields.key;
    assert(typeof key === 'string');

    const form = new FormData();
    Object.entries(presignedPost.fields).forEach(([k, v]) => {
        assert(typeof v === 'string');
        form.append(k, v);
    });
    form.append('file', file);

    // Send the POST request
    const s3Response = await fetch(presignedPost.url, {
        method: 'POST',
        body: form,
    });
    if (!s3Response.ok) {
        throw new CustomError('Failed presigned POST upload to S3', {
            presignedPost,
            key,
            response: s3Response,
        });
    }

    return {
        key,
        size: file.size,
        type: file.type,
        name: file.name,
    };
};
